import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Button,
  Badge,
  Input,
  Label,
  Textarea,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  Spinner,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@rnd-payplus/alloy-react';
import { Plus, Pencil, Trash2, Sparkles, ArrowRight, Search, LayoutGrid, TableIcon, FlaskConical } from 'lucide-react';
import { toast } from 'sonner';
import { KPICard } from '@rnd-payplus/alloy-react/patterns';
import { usePageShell } from '../contexts/PageShellContext';
import { AIMappingContent } from './AIMappingPage';
import {
  ReactFlow,
  Background,
  Controls,
  Handle,
  Position,
  type Node,
  type Edge,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

interface FieldMapping {
  source: string;
  target: string;
  type: string;
  transformation?: string;
}

interface TransformationMapping {
  id: string;
  name: string;
  sourceSystem: string;
  targetSystem: string;
  fieldMappings: FieldMapping[];
  active: boolean;
}

interface CdmRegistryEntry {
  id: string;
  label: string;
}
interface CdmRegistry {
  sources: CdmRegistryEntry[];
  targets: CdmRegistryEntry[];
}
interface CdmSchemaField {
  path: string;
  type: string;
}
interface CdmBuildResponse {
  data?: unknown;
  target?: string;
  cdmVersion?: string;
  error?: string;
  stage?: 'source' | 'target' | 'persist';
  issues?: Array<{ path: (string | number)[]; message: string }>;
}

interface CdmPersistResult {
  supplier_id: string;
  beneficiary_id: string;
  payment_run_id: string;
  invoice_ids: string[];
  payment_id: string;
  payment_instruction_id: string;
}
interface CdmPersistResponse {
  data?: CdmPersistResult;
  target?: string;
  cdmVersion?: string;
  error?: string;
  stage?: 'source' | 'target' | 'persist';
  issues?: Array<{ path: (string | number)[]; message: string }>;
}

// Mirrors CDM-WORK0ITEM/test2mult.xml for the "Test against sample" button.
const SAMPLE_SAP_PAYMENT_FILE_XML = `<?xml version="1.0" encoding="UTF-8"?>
<PaymentFile>
  <FileHeader>
    <PymnRunID>PLUS2</PymnRunID>
    <PymnRunDate>20260403</PymnRunDate>
    <CompanyCode>1000</CompanyCode>
    <Vendor>0000001000</Vendor>
    <VendorName>C.E.B. BERLIN</VendorName>
    <PymtMethod>+</PymtMethod>
    <TotPymtVendor>121.25</TotPymtVendor>
    <BenBankCountry>DE</BenBankCountry>
    <ISOCode>DE</ISOCode>
    <BankKey>50070010</BankKey>
    <BankAcc/>
    <BankAccHolder/>
    <IBAN/>
    <SWIFT/>
    <HouseBank>1000</HouseBank>
    <HouseBankNum>50070010</HouseBankNum>
    <HouseBankAcc>1000</HouseBankAcc>
    <RefToPaymentDoc>1000F1100000012026</RefToPaymentDoc>
    <CreationDate>20260408</CreationDate>
    <VendorEmail>email@vendor.com</VendorEmail>
    <VendorStreet>Kolping Str. 15</VendorStreet>
    <VendorCity>Berlin</VendorCity>
    <VendorRegion>11</VendorRegion>
    <VendorPsCode>12001</VendorPsCode>
    <VendorCountry>DE</VendorCountry>
    <VendorTaxNm/>
    <ValueDate>20260403</ValueDate>
  </FileHeader>
  <Payments>
    <PurchDoc/>
    <Description/>
    <PostingDate>20260403</PostingDate>
    <DOC2R>100019000000112026001</DOC2R>
    <Vendor>0000001000</Vendor>
    <SapAccDocNum>1900000011</SapAccDocNum>
    <DocType>KR</DocType>
    <FiscalYear>2026</FiscalYear>
    <CompCode>1000</CompCode>
    <ExtInvRefNum>TEST PAYPLUS2</ExtInvRefNum>
    <DocDate>20260101</DocDate>
    <BaseDate>20260112</BaseDate>
    <PymnTerms>ZB10</PymnTerms>
    <PymtCurrency>EUR</PymtCurrency>
    <AmountGross>50.00</AmountGross>
    <TaxAmount>0.00</TaxAmount>
    <NetAmount>48.50</NetAmount>
    <CashDiscAmount>1.50</CashDiscAmount>
    <WithTaxAmount>0.00</WithTaxAmount>
    <Currency>EUR</Currency>
    <GLAcc>0000160000</GLAcc>
    <CostCenter/>
    <TaxCode>V0</TaxCode>
    <PayPlusAppr/>
  </Payments>
  <Payments>
    <PurchDoc/>
    <Description/>
    <PostingDate>20260403</PostingDate>
    <DOC2R>100019000000122026001</DOC2R>
    <Vendor>0000001000</Vendor>
    <SapAccDocNum>1900000012</SapAccDocNum>
    <DocType>KR</DocType>
    <FiscalYear>2026</FiscalYear>
    <CompCode>1000</CompCode>
    <ExtInvRefNum>TEST PAYPLUS3</ExtInvRefNum>
    <DocDate>20260101</DocDate>
    <BaseDate>20260113</BaseDate>
    <PymnTerms>ZB10</PymnTerms>
    <PymtCurrency>EUR</PymtCurrency>
    <AmountGross>75.00</AmountGross>
    <TaxAmount>0.00</TaxAmount>
    <NetAmount>72.75</NetAmount>
    <CashDiscAmount>2.25</CashDiscAmount>
    <WithTaxAmount>0.00</WithTaxAmount>
    <Currency>EUR</Currency>
    <GLAcc>0000160000</GLAcc>
    <CostCenter/>
    <TaxCode>V0</TaxCode>
    <PayPlusAppr/>
  </Payments>
  <FileFooter>
    <TotalAmount>125.00</TotalAmount>
    <PaymentCount>2</PaymentCount>
  </FileFooter>
</PaymentFile>`;

// --- React Flow custom nodes for field mapping visualization ---

function SourceFieldNode({ data }: NodeProps) {
  return (
    <div className="rounded-lg border bg-card px-4 py-2 shadow-sm min-w-[160px]">
      <div className="flex items-center gap-2">
        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
          Source
        </Badge>
        <span className="text-sm font-medium">{data.label as string}</span>
      </div>
      <Handle type="source" position={Position.Right} className="!bg-blue-500 !w-2.5 !h-2.5" />
    </div>
  );
}

function TargetFieldNode({ data }: NodeProps) {
  return (
    <div className="rounded-lg border bg-card px-4 py-2 shadow-sm min-w-[160px]">
      <Handle type="target" position={Position.Left} className="!bg-green-500 !w-2.5 !h-2.5" />
      <div className="flex items-center gap-2">
        <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
          Target
        </Badge>
        <span className="text-sm font-medium">{data.label as string}</span>
      </div>
    </div>
  );
}

const mappingNodeTypes = {
  sourceField: SourceFieldNode,
  targetField: TargetFieldNode,
};

const DataMappingsPage = () => {
  const [activeTab, setActiveTab] = useState('mappings');
  const [mappings, setMappings] = useState<TransformationMapping[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMapping, setEditMapping] = useState<TransformationMapping | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [mappingToDelete, setMappingToDelete] = useState<TransformationMapping | null>(null);
  const [deletingMapping, setDeletingMapping] = useState(false);
  const [connections, setConnections] = useState<any[]>([]);

  const [formData, setFormData] = useState({
    name: '',
    sourceSystem: '',
    targetSystem: '',
  });

  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>([
    { source: '', target: '', type: 'string' },
  ]);

  const [viewMode, setViewMode] = useState<'table' | 'flow'>('table');
  const [selectedMappingId, setSelectedMappingId] = useState<string | null>(null);

  // Build React Flow nodes/edges for the selected mapping
  const selectedMapping = useMemo(
    () => mappings.find((m) => m.id === selectedMappingId) || mappings[0] || null,
    [mappings, selectedMappingId]
  );

  const { flowNodes, flowEdges } = useMemo(() => {
    if (!selectedMapping || !Array.isArray(selectedMapping.fieldMappings)) {
      return { flowNodes: [] as Node[], flowEdges: [] as Edge[] };
    }

    const fields = selectedMapping.fieldMappings.filter(
      (f: FieldMapping) => f.source && f.target
    );

    const nodeSpacingY = 70;
    const sourceX = 0;
    const targetX = 450;
    const startY = 0;

    const nodes: Node[] = [];
    const edges: Edge[] = [];

    fields.forEach((f: FieldMapping, i: number) => {
      const sourceId = `src-${i}`;
      const targetId = `tgt-${i}`;

      nodes.push({
        id: sourceId,
        type: 'sourceField',
        position: { x: sourceX, y: startY + i * nodeSpacingY },
        data: { label: f.source },
      });

      nodes.push({
        id: targetId,
        type: 'targetField',
        position: { x: targetX, y: startY + i * nodeSpacingY },
        data: { label: f.target },
      });

      edges.push({
        id: `edge-${i}`,
        source: sourceId,
        target: targetId,
        animated: true,
        style: { stroke: '#6366f1', strokeWidth: 2 },
        label: f.type !== 'string' ? f.type : undefined,
      });
    });

    return { flowNodes: nodes, flowEdges: edges };
  }, [selectedMapping]);

  const [aiWizardOpen, setAiWizardOpen] = useState(false);
  const [aiStep, setAiStep] = useState(1);
  const [aiSourceSample, setAiSourceSample] = useState('');
  const [aiTargetSample, setAiTargetSample] = useState('');
  const [aiFormat, setAiFormat] = useState<'auto' | 'json' | 'csv' | 'xml'>('auto');
  const [aiGenerating, setAiGenerating] = useState(false);

  // CDM mode state
  const [cdmMode, setCdmMode] = useState(false);
  const [cdmRegistry, setCdmRegistry] = useState<CdmRegistry | null>(null);
  const [cdmSource, setCdmSource] = useState('');
  const [cdmTarget, setCdmTarget] = useState('');
  const [cdmSourceFields, setCdmSourceFields] = useState<CdmSchemaField[]>([]);
  const [cdmTargetFields, setCdmTargetFields] = useState<CdmSchemaField[]>([]);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testRunning, setTestRunning] = useState(false);
  const [testResult, setTestResult] = useState<CdmBuildResponse | null>(null);

  const [persistConfirmOpen, setPersistConfirmOpen] = useState(false);
  const [persistRunning, setPersistRunning] = useState(false);
  const [persistResult, setPersistResult] = useState<CdmPersistResponse | null>(null);

  useEffect(() => {
    loadMappings();
    loadConnections();
  }, []);

  useEffect(() => {
    if (!cdmMode || cdmRegistry) return;
    fetch('/api/cdm/registry')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`registry ${r.status}`))))
      .then((data: CdmRegistry) => {
        setCdmRegistry(data);
        // Default to the first source/target so the typeaheads have something
        // to bind to immediately.
        if (!cdmSource && data.sources?.[0]) setCdmSource(data.sources[0].id);
        if (!cdmTarget && data.targets?.[0]) setCdmTarget(data.targets[0].id);
      })
      .catch((err) => {
        console.error('Failed to load CDM registry:', err);
        toast.error('Failed to load CDM registry. Is the transformation service running?');
      });
  }, [cdmMode, cdmRegistry, cdmSource, cdmTarget]);

  useEffect(() => {
    if (!cdmMode || !cdmSource) return;
    fetch(`/api/cdm/schema?source=${encodeURIComponent(cdmSource)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`schema ${r.status}`))))
      .then((data: { fields: CdmSchemaField[] }) => setCdmSourceFields(data.fields ?? []))
      .catch((err) => console.error('Failed to load source schema:', err));
  }, [cdmMode, cdmSource]);

  useEffect(() => {
    if (!cdmMode || !cdmTarget) return;
    fetch(`/api/cdm/schema?target=${encodeURIComponent(cdmTarget)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`schema ${r.status}`))))
      .then((data: { fields: CdmSchemaField[] }) => setCdmTargetFields(data.fields ?? []))
      .catch((err) => console.error('Failed to load target schema:', err));
  }, [cdmMode, cdmTarget]);

  useEffect(() => {
    if (!cdmMode) return;
    setFormData((prev) => ({ ...prev, sourceSystem: cdmSource, targetSystem: cdmTarget }));
  }, [cdmMode, cdmSource, cdmTarget]);

  const runCdmPersist = useCallback(async () => {
    if (!cdmSource || !cdmTarget) return;
    setPersistRunning(true);
    setPersistResult(null);
    try {
      const res = await fetch('/api/cdm/persist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // TODO: replace with real auth context
          'X-User-ID': '00000000-0000-0000-0000-000000000001',
          'X-Tenant-ID': 'default',
        },
        body: JSON.stringify({
          source: cdmSource,
          target: cdmTarget,
          payload: cdmSource === 'sap-payment-file' ? SAMPLE_SAP_PAYMENT_FILE_XML : null,
        }),
      });
      const data: CdmPersistResponse = await res.json();
      setPersistResult(data);
      if (res.ok) {
        toast.success('Persisted to cdm.* — payment is in draft / instruction pending');
      } else {
        toast.error(`Persist failed (${data.stage ?? 'unknown'} stage)`);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setPersistResult({ error: msg });
      toast.error(`Persist error: ${msg}`);
    } finally {
      setPersistRunning(false);
      setPersistConfirmOpen(false);
    }
  }, [cdmSource, cdmTarget]);

  const runCdmTest = useCallback(async () => {
    if (!cdmSource || !cdmTarget) {
      toast.error('Pick a CDM source and target first');
      return;
    }
    setTestRunning(true);
    setTestResult(null);
    setTestDialogOpen(true);
    try {
      const res = await fetch('/api/cdm/build', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source: cdmSource,
          target: cdmTarget,
          payload: cdmSource === 'sap-payment-file' ? SAMPLE_SAP_PAYMENT_FILE_XML : null,
        }),
      });
      const data: CdmBuildResponse = await res.json();
      setTestResult(data);
      if (res.ok) {
        toast.success('CDM build OK');
      } else {
        toast.error(`CDM build failed (${data.stage ?? 'unknown'} stage)`);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setTestResult({ error: msg });
      toast.error(`CDM build error: ${msg}`);
    } finally {
      setTestRunning(false);
    }
  }, [cdmSource, cdmTarget]);

  const loadMappings = async () => {
    try {
      const response = await fetch('/api/mappings');
      const data = await response.json();

      // Map database fields to component format
      const mappedData = data.map((m: any) => ({
        id: m.id,
        name: m.name,
        sourceSystem: m.source_system,
        targetSystem: m.target_system,
        fieldMappings: m.field_mappings?.mappings || [],
        active: m.active !== false,
      }));

      setMappings(mappedData);
    } catch (error) {
      console.error('Error loading mappings:', error);
    }
  };

  const confirmDeleteMapping = async () => {
    if (!mappingToDelete) return;
    setDeletingMapping(true);
    try {
      const res = await fetch(`/api/mappings/${mappingToDelete.id}`, { method: 'DELETE' });
      if (!res.ok) {
        throw new Error(`Failed to delete mapping (${res.status})`);
      }
      toast.success('Mapping deleted');
      setDeleteDialogOpen(false);
      setMappingToDelete(null);
      loadMappings();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete mapping');
    } finally {
      setDeletingMapping(false);
    }
  };

  const loadConnections = async () => {
    try {
      const response = await fetch('/api/connections');
      const data = await response.json();
      setConnections(data);
    } catch (error) {
      console.error('Error loading connections:', error);
    }
  };

  const addFieldMapping = () => {
    setFieldMappings([...fieldMappings, { source: '', target: '', type: 'string' }]);
  };

  const updateFieldMapping = (index: number, field: keyof FieldMapping, value: string) => {
    const updated = [...fieldMappings];
    updated[index] = { ...updated[index], [field]: value };
    setFieldMappings(updated);
  };

  const removeFieldMapping = (index: number) => {
    setFieldMappings(fieldMappings.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    try {
      const mapping = {
        name: formData.name,
        sourceSystem: formData.sourceSystem,
        targetSystem: formData.targetSystem,
        fieldMappings: fieldMappings.filter((f) => f.source && f.target),
      };

      const method = editMapping ? 'PUT' : 'POST';
      const url = editMapping ? `/api/mappings/${editMapping.id}` : '/api/mappings';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(mapping),
      });

      if (response.ok) {
        toast(editMapping ? 'Mapping updated!' : 'Mapping created!');
        setDialogOpen(false);
        setEditMapping(null);
        loadMappings();

        // Reset form
        setFormData({ name: '', sourceSystem: '', targetSystem: '' });
        setFieldMappings([{ source: '', target: '', type: 'string' }]);
      } else {
        toast.error('Failed to save mapping');
      }
    } catch (error) {
      console.error('Error saving mapping:', error);
      toast.error('Error saving mapping');
    }
  };

  const handleAiGenerate = async () => {
    if (!aiSourceSample || !aiTargetSample) {
      toast.error('Please provide both source and target samples');
      return;
    }
    setAiGenerating(true);
    try {
      const res = await fetch('/api/ai-mapping/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sourceSample: aiSourceSample,
          targetSample: aiTargetSample,
          sourceFormat: aiFormat,
          targetFormat: aiFormat,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        toast.error('AI generation failed: ' + (data.error ?? 'Unknown error'));
        return;
      }
      // Map AI results to FieldMapping format; mark low-confidence with a note
      const generated: FieldMapping[] = data.suggestedMappings.map(
        (m: { source: string; target: string; confidence: string; score: number }) => ({
          source: m.source,
          target: m.target,
          type: 'string',
          transformation:
            m.confidence === 'low' ? `low-confidence (${m.score}%)` : undefined,
        })
      );
      setFieldMappings(generated);
      setAiWizardOpen(false);
      setAiStep(1);
      setAiSourceSample('');
      setAiTargetSample('');
    } catch (err) {
      toast.error('AI generation error. Check that ANTHROPIC_API_KEY is configured.');
      console.error(err);
    } finally {
      setAiGenerating(false);
    }
  };

  // Common SAP field suggestions
  const sapFields = [
    'documentNumber',
    'runDate',
    'runId',
    'companyCode',
    'supplier',
    'recipient',
    'currency',
    'name',
    'street',
    'city',
    'postalCode',
    'country',
  ];

  // Common Pay Backend field suggestions
  const payFields = [
    'paymentReference',
    'currency',
    'beneficiaryName',
    'addressLine1',
    'city',
    'postalCode',
    'country',
    'debtorCompanyCode',
    'creditorId',
    'amount',
    'paymentRail',
    'purpose',
  ];

  const totalMappings = mappings.length;
  const uniqueSources = new Set(mappings.map(m => m.sourceSystem)).size;
  const uniqueTargets = new Set(mappings.map(m => m.targetSystem)).size;
  const totalFieldsMapped = mappings.reduce(
    (sum, m) => sum + (Array.isArray(m.fieldMappings) ? m.fieldMappings.length : 0),
    0
  );

  usePageShell({
    title: 'Mappings',
    primaryAction: activeTab === 'mappings' ? (
      <Button onClick={() => setDialogOpen(true)}>
        <Plus className="mr-2 h-4 w-4" />
        Create Mapping
      </Button>
    ) : undefined,
    breadcrumbs: [
      { label: 'Home', href: '/dashboard' },
      { label: 'Mappings' },
    ],
    updateKey: activeTab,
  });

  return (
    <div>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList>
          <TabsTrigger value="mappings">Field Mappings</TabsTrigger>
          <TabsTrigger value="ai-mapper">AI Mapper</TabsTrigger>
        </TabsList>

        <TabsContent value="ai-mapper" className="mt-6">
          <AIMappingContent />
        </TabsContent>

        <TabsContent value="mappings" className="mt-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <KPICard title="Total" value={totalMappings} subtitle="Mappings" />
        <KPICard title="Source Systems" value={uniqueSources} subtitle="Unique" />
        <KPICard title="Target Systems" value={uniqueTargets} subtitle="Unique" />
        <KPICard title="Fields Mapped" value={totalFieldsMapped} subtitle="Total" />
      </div>

      <div className="mb-5">
        <h2 className="text-base font-medium leading-6 mb-4">Available Connections</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {connections.map((conn) => (
            <div key={conn.id} className="rounded-lg border p-4">
                <p className="font-medium">{conn.platform}</p>
                <p className="text-sm text-muted-foreground">{conn.name}</p>
                <Badge
                  variant={conn.status === 'connected' ? 'default' : 'secondary'}
                  className="mt-2"
                >
                  {conn.status}
                </Badge>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-medium leading-6">Active Mappings</h2>
          <div className="flex gap-2">
            {viewMode === 'flow' && mappings.length > 1 && (
              <Select
                value={selectedMappingId || mappings[0]?.id || ''}
                onValueChange={(val) => setSelectedMappingId(val)}
              >
                <SelectTrigger className="w-[220px] h-8">
                  <SelectValue placeholder="Select mapping" />
                </SelectTrigger>
                <SelectContent>
                  {mappings.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setViewMode(viewMode === 'table' ? 'flow' : 'table')}
            >
              {viewMode === 'table' ? (
                <>
                  <LayoutGrid className="mr-2 h-4 w-4" />
                  Flow View
                </>
              ) : (
                <>
                  <TableIcon className="mr-2 h-4 w-4" />
                  Table View
                </>
              )}
            </Button>
          </div>
        </div>
        <div>
          {viewMode === 'flow' ? (
            <div>
              {selectedMapping ? (
                <>
                  <p className="text-sm text-muted-foreground mb-2">
                    {selectedMapping.name}: {selectedMapping.sourceSystem} → {selectedMapping.targetSystem}
                  </p>
                  <div className="h-[500px] rounded-md border">
                    <ReactFlow
                      nodes={flowNodes}
                      edges={flowEdges}
                      nodeTypes={mappingNodeTypes}
                      fitView
                      proOptions={{ hideAttribution: true }}
                    >
                      <Background />
                      <Controls />
                    </ReactFlow>
                  </div>
                </>
              ) : (
                <p className="text-center text-muted-foreground py-12">
                  No mappings available. Create one to see the flow visualization.
                </p>
              )}
            </div>
          ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Mapping Name</TableHead>
                <TableHead>Source System</TableHead>
                <TableHead></TableHead>
                <TableHead>Target System</TableHead>
                <TableHead>Fields Mapped</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mappings.map((mapping) => (
                <TableRow key={mapping.id}>
                  <TableCell>{mapping.name}</TableCell>
                  <TableCell>{mapping.sourceSystem}</TableCell>
                  <TableCell>
                    <ArrowRight className="h-4 w-4 text-primary" />
                  </TableCell>
                  <TableCell>{mapping.targetSystem}</TableCell>
                  <TableCell>{mapping.fieldMappings?.length || 0} fields</TableCell>
                  <TableCell>
                    <Badge variant={mapping.active ? 'default' : 'secondary'}>
                      {mapping.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setEditMapping(mapping);
                          setFormData({
                            name: mapping.name,
                            sourceSystem: mapping.sourceSystem,
                            targetSystem: mapping.targetSystem,
                          });

                          // Extract field mappings - handle different structures
                          let fieldMaps: FieldMapping[] = [];
                          if (Array.isArray(mapping.fieldMappings)) {
                            fieldMaps = mapping.fieldMappings;
                          } else if (
                            mapping.fieldMappings &&
                            typeof mapping.fieldMappings === 'object' &&
                            'mappings' in mapping.fieldMappings &&
                            Array.isArray(
                              (mapping.fieldMappings as { mappings: FieldMapping[] }).mappings
                            )
                          ) {
                            fieldMaps = (
                              mapping.fieldMappings as { mappings: FieldMapping[] }
                            ).mappings;
                          } else if (
                            mapping.fieldMappings &&
                            typeof mapping.fieldMappings === 'object'
                          ) {
                            fieldMaps = Object.values(mapping.fieldMappings);
                          }

                          setFieldMappings(
                            fieldMaps.length > 0
                              ? fieldMaps
                              : [{ source: '', target: '', type: 'string' }]
                          );
                          setDialogOpen(true);
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive"
                        title="Delete mapping"
                        aria-label="Delete mapping"
                        onClick={() => {
                          setMappingToDelete(mapping);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {mappings.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground">
                    No mappings defined. Create one to transform data between systems.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          )}
        </div>
      </div>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Mapping Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Data Transformation Mapping</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Mode toggle — switches the dialog between free-form (current
                 behaviour) and CDM-driven authoring. CDM mode wires the
                 source/target selects + field typeaheads to the live registry
                 from the transformation service. */}
            <div className="flex items-center gap-2">
              <Label className="text-sm">Mode:</Label>
              <Button
                size="sm"
                variant={cdmMode ? 'outline' : 'default'}
                onClick={() => setCdmMode(false)}
              >
                Free-form
              </Button>
              <Button
                size="sm"
                variant={cdmMode ? 'default' : 'outline'}
                onClick={() => setCdmMode(true)}
              >
                Common Data Model
              </Button>
            </div>

            <div>
              <Label htmlFor="mapping-name">Mapping Name</Label>
              <Input
                id="mapping-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., SAP F110 to Tungsten Pay+"
              />
            </div>

            {cdmMode ? (
              // ── CDM mode: registry-driven source/target ────────────────
              <>
                <div>
                  <Label htmlFor="cdm-source">CDM Source</Label>
                  <Select value={cdmSource} onValueChange={setCdmSource}>
                    <SelectTrigger id="cdm-source">
                      <SelectValue placeholder="Select CDM source" />
                    </SelectTrigger>
                    <SelectContent>
                      {(cdmRegistry?.sources ?? []).map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.label} <span className="text-muted-foreground">({s.id})</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="cdm-target">CDM Target</Label>
                  <Select value={cdmTarget} onValueChange={setCdmTarget}>
                    <SelectTrigger id="cdm-target">
                      <SelectValue placeholder="Select CDM target" />
                    </SelectTrigger>
                    <SelectContent>
                      {(cdmRegistry?.targets ?? []).map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.label} <span className="text-muted-foreground">({t.id})</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            ) : (
              // ── Free-form mode: hardcoded system selects (legacy) ──────
              <>
                <div>
                  <Label htmlFor="source-system">Source System</Label>
                  <Select
                    value={formData.sourceSystem}
                    onValueChange={(value) =>
                      setFormData({ ...formData, sourceSystem: value })
                    }
                  >
                    <SelectTrigger id="source-system">
                      <SelectValue placeholder="Select source system" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SAP F110">SAP F110</SelectItem>
                      <SelectItem value="SAP F111">SAP F111</SelectItem>
                      <SelectItem value="Oracle Fusion">Oracle Fusion</SelectItem>
                      <SelectItem value="NetSuite">NetSuite</SelectItem>
                      <SelectItem value="Tungsten Network">Tungsten Network</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="target-system">Target System</Label>
                  <Select
                    value={formData.targetSystem}
                    onValueChange={(value) =>
                      setFormData({ ...formData, targetSystem: value })
                    }
                  >
                    <SelectTrigger id="target-system">
                      <SelectValue placeholder="Select target system" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Tungsten Pay+">Tungsten Pay+</SelectItem>
                      <SelectItem value="SAP ERP">SAP ERP</SelectItem>
                      <SelectItem value="Oracle Fusion">Oracle Fusion</SelectItem>
                      <SelectItem value="NetSuite">NetSuite</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                onClick={async () => {
                  if (!formData.sourceSystem || !formData.targetSystem) {
                    toast.error('Please select both source and target systems first');
                    return;
                  }
                  try {
                    const response = await fetch('/api/mapping/auto-detect', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        sourceSystem: formData.sourceSystem,
                        targetSystem: formData.targetSystem,
                      }),
                    });
                    const data = await response.json();

                    // Set auto-detected field mappings
                    setFieldMappings(
                      data.autoDetected.map((m: any) => ({
                        source: m.source,
                        target: m.target,
                        type: m.type || 'string',
                      }))
                    );

                    toast(`Auto-detected ${data.autoDetected.length} field mappings!`);
                  } catch (error) {
                    console.error('Auto-detect error:', error);
                    toast.error('Auto-detect failed. Please map fields manually.');
                  }
                }}
                disabled={!formData.sourceSystem || !formData.targetSystem}
              >
                <Search className="mr-2 h-4 w-4" />
                Auto-Detect Field Mappings
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setAiWizardOpen(true);
                  setAiStep(1);
                }}
              >
                <Sparkles className="mr-2 h-4 w-4" />
                Auto-Generate with AI
              </Button>
              {cdmMode && (
                <Button
                  variant="outline"
                  onClick={runCdmTest}
                  disabled={!cdmSource || !cdmTarget || testRunning}
                  title="Run /api/cdm/build with the selected source/target and the bundled fixture"
                >
                  {testRunning ? (
                    <Spinner className="mr-2 h-4 w-4" />
                  ) : (
                    <FlaskConical className="mr-2 h-4 w-4" />
                  )}
                  Test against sample
                </Button>
              )}
            </div>

            <h3 className="text-lg font-semibold mt-6">Field Mappings</h3>

            {fieldMappings.map((mapping, index) => (
              <div key={index} className="rounded-lg border p-4">
                <div className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-4">
                    <Label>Source Field</Label>
                    {cdmMode ? (
                      // Datalist-backed typeahead — fast on hundreds of items.
                      <>
                        <Input
                          list={`cdm-source-fields-${index}`}
                          value={mapping.source}
                          onChange={(e) =>
                            updateFieldMapping(index, 'source', e.target.value)
                          }
                          placeholder="e.g. header.vendor_name"
                        />
                        <datalist id={`cdm-source-fields-${index}`}>
                          {cdmSourceFields.map((f) => (
                            <option key={f.path} value={f.path}>
                              {f.type}
                            </option>
                          ))}
                        </datalist>
                      </>
                    ) : (
                      <Select
                        value={mapping.source}
                        onValueChange={(value) =>
                          updateFieldMapping(index, 'source', value)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select source field" />
                        </SelectTrigger>
                        <SelectContent>
                          {sapFields.map((field) => (
                            <SelectItem key={field} value={field}>
                              {field}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                  <div className="col-span-1 flex justify-center pt-5">
                    <ArrowRight className="h-4 w-4" />
                  </div>
                  <div className="col-span-4">
                    <Label>Target Field</Label>
                    {cdmMode ? (
                      <>
                        <Input
                          list={`cdm-target-fields-${index}`}
                          value={mapping.target}
                          onChange={(e) =>
                            updateFieldMapping(index, 'target', e.target.value)
                          }
                          placeholder="e.g. supplier.name"
                        />
                        <datalist id={`cdm-target-fields-${index}`}>
                          {cdmTargetFields.map((f) => (
                            <option key={f.path} value={f.path}>
                              {f.type}
                            </option>
                          ))}
                        </datalist>
                      </>
                    ) : (
                      <Select
                        value={mapping.target}
                        onValueChange={(value) =>
                          updateFieldMapping(index, 'target', value)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select target field" />
                        </SelectTrigger>
                        <SelectContent>
                          {payFields.map((field) => (
                            <SelectItem key={field} value={field}>
                              {field}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                  <div className="col-span-2">
                    <Label>Type</Label>
                    <Select
                      value={mapping.type}
                      onValueChange={(value) =>
                        updateFieldMapping(index, 'type', value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="string">String</SelectItem>
                        <SelectItem value="number">Number</SelectItem>
                        <SelectItem value="date">Date</SelectItem>
                        <SelectItem value="currency">Currency</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-1 flex justify-center pt-5">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive"
                      onClick={() => removeFieldMapping(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}

            <Button variant="outline" onClick={addFieldMapping} className="mt-2">
              <Plus className="mr-2 h-4 w-4" />
              Add Field Mapping
            </Button>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleSave}>Save Mapping</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Mapping Wizard */}
      <Dialog open={aiWizardOpen} onOpenChange={setAiWizardOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Auto-Generate Mappings with AI</DialogTitle>
            <DialogDescription>
              Step {aiStep} of 2 — {aiStep === 1 ? 'Source Schema' : 'Target Schema'}
            </DialogDescription>
          </DialogHeader>

          {aiStep === 1 ? (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Paste a JSON, CSV, or XML sample from the <strong>source system</strong>.
                The AI will extract all field names automatically.
              </p>
              <div>
                <Label htmlFor="ai-format">Format</Label>
                <Select
                  value={aiFormat}
                  onValueChange={(value) => setAiFormat(value as typeof aiFormat)}
                >
                  <SelectTrigger id="ai-format">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto-Detect</SelectItem>
                    <SelectItem value="json">JSON</SelectItem>
                    <SelectItem value="csv">CSV</SelectItem>
                    <SelectItem value="xml">XML</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="ai-source">Source sample data</Label>
                <Textarea
                  id="ai-source"
                  value={aiSourceSample}
                  onChange={(e) => setAiSourceSample(e.target.value)}
                  rows={10}
                  placeholder='{"documentNumber": "INV-001", "currency": "EUR", "amount": 1500.00, ...}'
                  className="font-mono"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Paste a JSON, CSV, or XML sample from the <strong>target system</strong>.
                The AI will suggest the best field mappings.
              </p>
              <div>
                <Label htmlFor="ai-target">Target sample data</Label>
                <Textarea
                  id="ai-target"
                  value={aiTargetSample}
                  onChange={(e) => setAiTargetSample(e.target.value)}
                  rows={10}
                  placeholder='{"paymentReference": "", "currency": "", "beneficiaryName": "", ...}'
                  className="font-mono"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            {aiStep === 1 ? (
              <Button onClick={() => setAiStep(2)} disabled={!aiSourceSample.trim()}>
                Next: Target Schema
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setAiStep(1)}>
                  Back
                </Button>
                <Button
                  onClick={handleAiGenerate}
                  disabled={!aiTargetSample.trim() || aiGenerating}
                >
                  {aiGenerating ? (
                    <>
                      <Spinner className="mr-2 h-4 w-4" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" />
                      Generate Mappings
                    </>
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CDM "Test against sample" result dialog */}
      <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>CDM Build Result</DialogTitle>
            <DialogDescription>
              {cdmSource} → {cdmTarget} (using bundled fixture)
            </DialogDescription>
          </DialogHeader>
          {testRunning ? (
            <div className="flex items-center justify-center py-12">
              <Spinner className="h-6 w-6" />
              <span className="ml-3 text-muted-foreground">Building CDM document…</span>
            </div>
          ) : testResult ? (
            <div className="space-y-3">
              {testResult.error ? (
                <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3">
                  <p className="font-medium text-destructive">
                    {testResult.stage ? `${testResult.stage} validation failed` : 'Build failed'}
                  </p>
                  <p className="text-sm mt-1">{testResult.error}</p>
                  {testResult.issues && testResult.issues.length > 0 && (
                    <ul className="mt-2 text-xs font-mono space-y-1">
                      {testResult.issues.slice(0, 20).map((i, idx) => (
                        <li key={idx}>
                          <span className="text-muted-foreground">{i.path.join('.')}:</span> {i.message}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : (
                <div className="rounded-lg border border-green-500/40 bg-green-500/5 p-3">
                  <p className="font-medium text-green-700">
                    CDM document valid (target: {testResult.target}, version {testResult.cdmVersion})
                  </p>
                </div>
              )}
              {testResult.data !== undefined && (
                <div>
                  <Label>Built CDM document</Label>
                  <pre className="mt-1 max-h-[420px] overflow-auto rounded-md border bg-muted/30 p-3 text-xs font-mono">
                    {JSON.stringify(testResult.data, null, 2)}
                  </pre>
                </div>
              )}
              {persistResult && (
                <div className={
                  persistResult.error
                    ? 'rounded-lg border border-destructive/40 bg-destructive/5 p-3'
                    : 'rounded-lg border border-green-500/40 bg-green-500/5 p-3'
                }>
                  {persistResult.error ? (
                    <>
                      <p className="font-medium text-destructive">Persist failed</p>
                      <p className="text-sm mt-1">{persistResult.error}</p>
                      {persistResult.issues && persistResult.issues.length > 0 && (
                        <ul className="mt-2 text-xs font-mono space-y-1">
                          {persistResult.issues.slice(0, 20).map((i, idx) => (
                            <li key={idx}>
                              <span className="text-muted-foreground">{i.path.join('.')}:</span> {i.message}
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="font-medium text-green-700">
                        Persisted to cdm.* — payment is draft, instruction is pending
                      </p>
                      {persistResult.data && (
                        <ul className="mt-2 text-xs font-mono space-y-1">
                          <li>supplier_id: {persistResult.data.supplier_id}</li>
                          <li>beneficiary_id: {persistResult.data.beneficiary_id}</li>
                          <li>payment_run_id: {persistResult.data.payment_run_id}</li>
                          <li>invoice_ids: {persistResult.data.invoice_ids.join(', ')}</li>
                          <li>payment_id: {persistResult.data.payment_id}</li>
                          <li>payment_instruction_id: {persistResult.data.payment_instruction_id}</li>
                        </ul>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          ) : null}
          <DialogFooter>
            {testResult && !testResult.error && cdmTarget === 'payplus-payment-request' && (
              <Button
                variant="default"
                onClick={() => setPersistConfirmOpen(true)}
                disabled={persistRunning}
              >
                {persistRunning ? <Spinner className="mr-2 h-4 w-4" /> : null}
                Persist to DB
              </Button>
            )}
            <DialogClose asChild>
              <Button variant="outline">Close</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Persist confirm dialog */}
      <Dialog open={persistConfirmOpen} onOpenChange={setPersistConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Persist CDM document to database?</DialogTitle>
            <DialogDescription>
              This writes 8 rows across cdm.suppliers, cdm.beneficiaries, cdm.payment_runs,
              cdm.invoices (+line items), cdm.payments (+line items), and cdm.payment_instructions
              in a single transaction.
            </DialogDescription>
          </DialogHeader>
          <p className="py-2 text-sm text-muted-foreground">
            The payment will land in <code>draft</code> state and the payment instruction in{' '}
            <code>pending</code>. A separate manual submit step is required to send it to PayPlus.
          </p>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" disabled={persistRunning}>Cancel</Button>
            </DialogClose>
            <Button onClick={runCdmPersist} disabled={persistRunning}>
              {persistRunning ? <Spinner className="mr-2 h-4 w-4" /> : null}
              Confirm persist
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete mapping</DialogTitle>
            <DialogDescription>This action cannot be undone.</DialogDescription>
          </DialogHeader>
          {mappingToDelete && (
            <p className="py-2 text-sm">
              Are you sure you want to delete{' '}
              <span className="font-medium">&ldquo;{mappingToDelete.name}&rdquo;</span>?
            </p>
          )}
          <DialogFooter className="gap-2">
            <DialogClose asChild>
              <Button variant="outline" disabled={deletingMapping}>Cancel</Button>
            </DialogClose>
            <Button variant="destructive" onClick={confirmDeleteMapping} disabled={deletingMapping}>
              {deletingMapping ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DataMappingsPage;
